import { useEffect, useRef } from "preact/hooks";
import uPlot from "uplot";
import "uplot/dist/uPlot.min.css";
import { SkeletonLoader } from "../SkeletonLoader";
import { ErrorMessage } from "../ErrorMessage";
import { formatBps } from "../../utils/format";
import {
  getChartColors,
  colorWithAlpha,
  isoToUnixSeconds,
  createTooltip,
  makeTimeFormatter,
  formatBpsAxis,
  useChartTheme,
} from "./chartUtils";

type ClientBandwidthChartProps = {
  data: Array<{
    timestamp: string;
    rate_download: number;
    rate_upload: number;
  }>;
  clientName: string;
  loading?: boolean;
  error?: string;
};

export function ClientBandwidthChart({
  data,
  clientName,
  loading,
  error,
}: ClientBandwidthChartProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<uPlot | null>(null);
  const tooltipRef = useRef<HTMLDivElement | null>(null);
  const theme = useChartTheme();

  useEffect(() => {
    if (loading || error || data.length === 0) return;
    if (!containerRef.current) return;

    const colors = getChartColors();
    const tooltip = createTooltip();
    tooltipRef.current = tooltip;
    containerRef.current.appendChild(tooltip);

    const timestamps = data.map((d) => isoToUnixSeconds(d.timestamp));
    const down = data.map((d) => d.rate_download);
    const up = data.map((d) => d.rate_upload);

    const width = containerRef.current.clientWidth;
    const height = 300;

    const opts: uPlot.Options = {
      width,
      height,
      cursor: { drag: { x: false, y: false } },
      legend: { show: true },
      axes: [
        {
          stroke: colors.text,
          grid: { stroke: colors.grid },
          ticks: { stroke: colors.grid },
          values: makeTimeFormatter(timestamps),
        },
        {
          stroke: colors.text,
          grid: { stroke: colors.grid },
          ticks: { stroke: colors.grid },
          values: formatBpsAxis,
          size: 70,
        },
      ],
      series: [
        {},
        {
          label: "Download",
          stroke: colors.blue,
          fill: colorWithAlpha(colors.blue, 0.15),
          width: 2,
        },
        {
          label: "Upload",
          stroke: colors.purple,
          fill: colorWithAlpha(colors.purple, 0.15),
          width: 2,
        },
      ],
      hooks: {
        setCursor: [
          (u) => {
            const idx = u.cursor.idx;
            if (idx == null || idx < 0) {
              tooltip.style.display = "none";
              return;
            }
            const t = u.data[0][idx];
            const downVal = u.data[1][idx];
            const upVal = u.data[2][idx];
            if (t == null || downVal == null || upVal == null) {
              tooltip.style.display = "none";
              return;
            }
            tooltip.innerHTML = `
              <div><strong>${clientName}</strong></div>
              <div><small>${new Date(t * 1000).toLocaleString("id-ID")}</small></div>
              <div style="color:${colors.blue}">Download: ${formatBps(downVal)}</div>
              <div style="color:${colors.purple}">Upload: ${formatBps(upVal)}</div>
            `;
            tooltip.style.display = "block";
            tooltip.style.left = `${(u.cursor.left ?? 0) + 12}px`;
            tooltip.style.top = `${(u.cursor.top ?? 0) + 12}px`;
          },
        ],
      },
    };

    const chart = new uPlot(opts, [timestamps, down, up], containerRef.current);
    chartRef.current = chart;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        chart.setSize({ width: entry.contentRect.width, height });
      }
    });
    resizeObserver.observe(containerRef.current);

    return () => {
      resizeObserver.disconnect();
      chart.destroy();
      tooltip.remove();
      chartRef.current = null;
      tooltipRef.current = null;
    };
  }, [data, clientName, theme, loading, error]);

  if (loading) return <SkeletonLoader type="chart" />;
  if (error) return <ErrorMessage message={error} />;
  if (data.length === 0) return <div class="chart-empty">Belum ada data</div>;

  return <div ref={containerRef} class="chart-container" />;
}
